import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { SectionHeader } from "./Home";
import { Search as SearchIcon, Clock, BookOpen } from "lucide-react";

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQ = searchParams.get("q") || "";
  const [q, setQ] = useState(initialQ);
  const [hits, setHits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const doSearch = useCallback((term) => {
    if (!term.trim()) { setHits([]); setSearched(false); return; }
    setLoading(true);
    api.get(`/search?q=${encodeURIComponent(term.trim())}`)
      .then((r) => setHits(r.data.hits || []))
      .catch(() => setHits([]))
      .finally(() => { setLoading(false); setSearched(true); });
  }, []);

  useEffect(() => {
    if (initialQ) doSearch(initialQ);
  }, [initialQ, doSearch]);

  const submit = (e) => {
    e.preventDefault();
    setSearchParams(q.trim() ? { q: q.trim() } : {});
    doSearch(q);
  };

  const epHits = hits.filter((h) => h.type === "episode");
  const secHits = hits.filter((h) => h.type === "section");

  return (
    <div data-testid="search-page">
      <SectionHeader title="Search" subtitle="Full-text across episodes + community notes" />

      <form onSubmit={submit} className="brutal-card p-4 mb-6 flex flex-col sm:flex-row gap-3">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="try 'pinot noir', 'werewolf', 'mikey'..."
          className="brutal-input font-mono-display"
          autoFocus
          data-testid="search-input"
        />
        <button type="submit" className="brutal-btn primary justify-center" data-testid="search-submit">
          <SearchIcon size={16} strokeWidth={3} /> Search
        </button>
      </form>

      {loading && (
        <div className="brutal-card p-8 font-mono-display uppercase tracking-widest text-center" data-testid="search-loading">
          Searching the hatch...
        </div>
      )}

      {!loading && searched && hits.length === 0 && (
        <div className="brutal-card p-8 text-center" data-testid="search-no-results">
          <div className="font-mono-display uppercase tracking-widest text-slate-600 mb-2">&gt; No matches</div>
          <div className="text-sm text-slate-700">No episodes or sections match "{q}".</div>
        </div>
      )}

      {!loading && hits.length > 0 && (
        <>
          <div className="font-mono-display text-xs uppercase tracking-widest text-slate-600 mb-4" data-testid="search-count">
            {hits.length} result{hits.length === 1 ? "" : "s"} · {epHits.length} episode{epHits.length === 1 ? "" : "s"} · {secHits.length} section{secHits.length === 1 ? "" : "s"}
          </div>

          {epHits.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center gap-2 font-mono-display text-xs uppercase tracking-widest mb-3">
                <BookOpen size={14} strokeWidth={3} /> Episodes
              </div>
              <ul className="space-y-3">
                {epHits.map((h) => (
                  <li key={`ep-${h.episode_id}`} className="brutal-card p-4" data-testid={`result-episode-${h.episode_id}`}>
                    <Link to={`/episodes/${h.episode_id}`} className="no-underline text-slate-900 block">
                      <div className="font-mono-display text-[10px] uppercase tracking-[0.25em] text-pink-600 mb-1">
                        S{h.season} · EP {h.episode_number}
                      </div>
                      <div className="font-bold text-lg mb-1">{h.episode_title}</div>
                      <div className="text-sm text-slate-700">{h.snippet}…</div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {secHits.length > 0 && (
            <div>
              <div className="flex items-center gap-2 font-mono-display text-xs uppercase tracking-widest mb-3">
                <Clock size={14} strokeWidth={3} /> Community Sections
              </div>
              <ul className="space-y-3">
                {secHits.map((h) => (
                  <li key={`sec-${h.section_id}`} className="brutal-card p-4" data-testid={`result-section-${h.section_id}`}>
                    <Link to={`/episodes/${h.episode_id}`} className="no-underline text-slate-900 block">
                      <div className="flex gap-3 items-center font-mono-display text-xs uppercase tracking-widest text-slate-600 mb-1">
                        <span className="bg-slate-900 text-white px-2 py-0.5">S{h.season}E{String(h.episode_number).padStart(2, "0")}</span>
                        <span className="bg-yellow-400 text-slate-900 px-2 py-0.5">⏱ {h.timestamp}</span>
                        <span className="truncate">{h.episode_title}</span>
                      </div>
                      <div className="font-bold text-lg mb-1">{h.headline}</div>
                      <div className="text-sm text-slate-700">{h.snippet}…</div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
